// Spec: docs/plans/frontend-stack.md §3.1 (selection priority).

import { useEffect, useState } from 'react';

const VALID_SKINS = ['modern', 'legacy'] as const;
export type SkinName = (typeof VALID_SKINS)[number];

const STORAGE_KEY = 'aw.skin';
const DEFAULT_SKIN: SkinName = 'modern';

function isSkinName(v: unknown): v is SkinName {
  return typeof v === 'string' && (VALID_SKINS as readonly string[]).includes(v);
}

function readPreference(): SkinName {
  if (typeof window === 'undefined') return DEFAULT_SKIN;

  // Priority 1: ?skin= query param (writes through to localStorage).
  const fromQuery = new URLSearchParams(window.location.search).get('skin');
  if (isSkinName(fromQuery)) {
    window.localStorage.setItem(STORAGE_KEY, fromQuery);
    return fromQuery;
  }

  // Priority 2: localStorage.
  const fromStorage = window.localStorage.getItem(STORAGE_KEY);
  if (isSkinName(fromStorage)) return fromStorage;

  // Priority 3 (future): users.skin column — hydrated server-side once auth ships.

  return DEFAULT_SKIN;
}

export function useSkin(): SkinName {
  const [skin, setSkin] = useState<SkinName>(readPreference);

  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY) setSkin(readPreference());
    }
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return skin;
}
